import { Component, OnInit, Injector, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { Router } from "@angular/router";
import { AuthenticationService } from "src/app/core/services/authentication.service";

import { MenuService } from "../../core/menu/menu.service";
import { SettingsService } from "../../core/settings/settings.service";
import { environment } from "../../../environments/environment";
import { IssuingEntity } from "src/app/core/models/issuingEntity";
import { IssuingEntitiesService } from "src/app/routes/issuance-partners/issuing-entities.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { IssuingEntityBenefitBalanceDTO } from "src/app/dtos/issuingEntityBenefitBalanceDTO";
import { User } from "src/app/core/models/user";
import moment from "moment/min/moment.min";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: Array<any>;
  router: Router;

  version = environment.version;
  @ViewChild("selectIssuingEntity") selectIssuingEntity: ElementRef;
  issuingEntity: IssuingEntity;
  benefits: IssuingEntityBenefitBalanceDTO[] = [];
  user: User;
  moment: any = moment;
  now: Date = new Date();
  private externalClickHandler: (e: MouseEvent) => void = null;

  constructor(
    public menu: MenuService,
    public settings: SettingsService,
    public injector: Injector,
    private authService: AuthenticationService,
    private issuingEntityService: IssuingEntitiesService,
    private modalService: NgbModal,
  ) {
    this.user = this.authService.currentUser();
    //this.menuItems = menu.getMenu();
    this.menuItems = this.user
      ? menu
          .getMenu()
          .filter((m) => m.roles.filter((r) => this.user.isInRole(r)).length > 0)
      : [];
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);
    this.router.events.subscribe((val) => {
      // close any submenu opened when route changes
      this.removeFloatingNav();
      // scroll view to top
      window.scrollTo(0, 0);
      // close sidebar on route change
      this.settings.setLayoutSetting("asideToggled", false);
    });

    this.issuingEntity =
      this.user.issuingEntites.length == 1
        ? this.user.issuingEntites[0]
        : this.authService.currentIssuingEntity();
    this.getIssueEntityBenefits(this.issuingEntity);

    this.issuingEntityService.getBalance.subscribe((bal) => {
      if (bal == true) {
        this.getIssueEntityBenefits(this.issuingEntity);
      }
    });
  }

  ngOnDestroy(): any {
    this.stopListeningForExternalClicks();
  }

  toggleSubmenuClick(event): any {
    event.preventDefault();

    if (
      !this.isSidebarCollapsed() &&
      !this.isSidebarCollapsedText() &&
      !this.isEnabledHover()
    ) {
      const ul = event.currentTarget.nextElementSibling as HTMLElement;

      // hide other submenus
      const parentNav = ul ? ul.parentElement.closest(".sidebar-subnav") : null;
      document.querySelectorAll<HTMLElement>(".sidebar-subnav").forEach((el) => {
        // if element is not a parent or self ul
        if (el !== parentNav && el !== ul) {
          this.closeMenu(el);
        }
      });

      // abort if not UL to process
      if (!ul) {
        return;
      }

      // any child menu should start closed
      ul.querySelectorAll<HTMLElement>(".sidebar-subnav").forEach((el) => {
        this.closeMenu(el);
      });

      // toggle UL height
      const ulHeight = window.getComputedStyle(ul).height;
      if (ulHeight === "auto" || parseInt(ulHeight, 10)) {
        this.closeMenu(ul);
      } else {
        // expand menu
        ul.addEventListener(
          "transitionend",
          () => {
            ul.style.height = "auto";
          },
          { once: true }
        );
        ul.style.height = ul.scrollHeight + "px";
        // add class to manage animation
        ul.classList.add("opening");
      }
    }
  }

  // Close menu collapsing height
  closeMenu(elem: HTMLElement): any {
    elem.style.height = elem.scrollHeight + "px"; // set height
    elem.style.height = "0px"; // and move to zero to collapse
    elem.classList.remove("opening");
  }

  toggleSubmenuHover(event: any): any {
    if (
      this.isSidebarCollapsed() ||
      this.isSidebarCollapsedText() ||
      this.isEnabledHover()
    ) {
      event.preventDefault();

      this.removeFloatingNav();

      const anchor = event.currentTarget as HTMLElement;
      const ul = anchor.nextElementSibling as HTMLElement;

      if (!ul) {
        return; // if not submenu return
      }

      const aside = document.querySelector(".aside-container") as HTMLElement;
      const asideInner = aside.querySelector(":scope > .aside-inner") as HTMLElement; // for top offset calculation
      const sidebar = asideInner.querySelector(":scope > .sidebar") as HTMLElement;
      const mar =
        parseInt(window.getComputedStyle(asideInner).paddingTop, 10) +
        parseInt(window.getComputedStyle(aside).paddingTop, 10);
      const item = anchor.parentElement;
      const itemTop =
        item.offsetTop -
        (parseFloat(window.getComputedStyle(item).marginTop) || 0) +
        mar -
        sidebar.scrollTop;

      const floatingNav = ul.cloneNode(true) as HTMLElement;
      aside.appendChild(floatingNav);
      const vwHeight = document.body.clientHeight;

      floatingNav.classList.add("nav-floating");

      // each item has ~40px height
      // multiply to force space for at least N items
      const safeOffsetValue = 40 * 5;
      const navHeight = this.outerHeightWithMargin(floatingNav) + 2; // 2px border
      const safeOffset =
        navHeight < safeOffsetValue ? navHeight : safeOffsetValue;

      const displacement = 25; // displacement in px from bottom

      // if not enough space to show N items, use then calculated 'safeOffset'
      const menuTop =
        vwHeight - itemTop > safeOffset
          ? itemTop
          : vwHeight - safeOffset - displacement;

      floatingNav.classList.remove("opening"); // necesary for demo if switched between normal//collapsed mode
      floatingNav.style.position = this.settings.getLayoutSetting("isFixed")
        ? "fixed"
        : "absolute";
      floatingNav.style.top = menuTop + "px";
      floatingNav.style.bottom =
        this.outerHeightWithMargin(floatingNav) + menuTop > vwHeight
          ? displacement + "px"
          : "auto";

      floatingNav.addEventListener("mouseleave", () => {
        floatingNav.remove();
      });
      floatingNav.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault(); // prevents page reload on click
          // get the exact route path to navigate
          const routeTo = link.getAttribute("route");
          if (routeTo) {
            this.router.navigate([routeTo]);
          }
        });
      });

      this.listenForExternalClicks();
    }
  }

  listenForExternalClicks(): any {
    this.externalClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.parentElement || !target.parentElement.closest(".aside-container")) {
        this.removeFloatingNav();
        this.stopListeningForExternalClicks();
      }
    };
    document.addEventListener("click", this.externalClickHandler);
  }

  private stopListeningForExternalClicks(): void {
    if (this.externalClickHandler) {
      document.removeEventListener("click", this.externalClickHandler);
      this.externalClickHandler = null;
    }
  }

  removeFloatingNav(): any {
    document.querySelectorAll(".nav-floating").forEach((el) => el.remove());
  }

  private outerHeightWithMargin(el: HTMLElement): number {
    const style = window.getComputedStyle(el);
    return (
      el.offsetHeight +
      (parseFloat(style.marginTop) || 0) +
      (parseFloat(style.marginBottom) || 0)
    );
  }

  isSidebarCollapsed(): any {
    return this.settings.getLayoutSetting("isCollapsed");
  }
  isSidebarCollapsedText(): any {
    return this.settings.getLayoutSetting("isCollapsedText");
  }
  isEnabledHover(): any {
    return this.settings.getLayoutSetting("asideHover");
  }

  changeIssuingEntity() {
    this.modalService.open(this.selectIssuingEntity, {
      centered: true,
      size: "md",
    });
  }

  getIssueEntityBenefits(issuingEntity: IssuingEntity) {
    if (issuingEntity) {
      this.issuingEntityService
        .getBenefits(issuingEntity.id)
        .subscribe((result) => {
          this.benefits = result;
          this.benefits.forEach((element) => {
            element.active = moment(this.now).isBetween(
              moment(element.startValidityDate),
              moment(element.endValidityDate)
            );
            element.expired = moment(this.now).isAfter(
              moment(element.endValidityDate)
            );
          });
        });
    }
  }

  onRefresh(item: any) {
    if(item == 'added') {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
}
